import { boot, createEraroError, eraroCodesMap } from '@dazn/bootstrap-lib';

import { BRANDS } from '../configuration/constants';

import { GetGuid, GetDevMode } from './common-types';
import { getHeaders } from './getHeaders';

const isValid = (tokenValue: Record<string, unknown>) =>
    tokenValue && Object.keys(tokenValue).length > 0;

export const decodeToken = (token: string) => {
    try {
        if (!token || token === '') return null;
        // Split the JWT into its parts (header, payload, signature)
        const parts = token.split('.');
        const jwt_parts = 3;
        // Ensure that the JWT has three parts
        if (parts.length !== jwt_parts) {
            throw new Error(
                'Invalid JWT: JWT should consist of three parts (header, payload, signature).',
            );
        }
        // Convert Base64URL to Base64 by replacing URL-safe characters
        let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');

        // Pad with '=' to ensure valid Base64 length
        const base64Modulo = 4;
        while (base64.length % base64Modulo) {
            base64 += '=';
        }

        // Decode base64 to byte array (Uint8Array)
        const binaryString = window.atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        // Decode the byte array to a string using TextDecoder
        const decoder = new TextDecoder('utf-8');
        const decodedToken = decoder.decode(bytes);
        const tokenValue = JSON.parse(decodedToken);
        if (!isValid(tokenValue)) {
            return null;
        }
        return tokenValue;
    } catch (error) {
        const error_message = JSON.stringify(error);
        window.newrelic?.addPageAction('JWT_Token_Parsing_Failed', {
            error_message,
            token,
        });
        return null;
    }
};

const pickToken = (json: { AuthToken?: { Token: string } }) =>
    json?.AuthToken?.Token;

export const HAS_SENT_APP_LAUNCH_RAT = 'hasSentAppLaunchRat';

export const checkToken = (
    token: string,
    authTokenRefreshUrl: string,
    sessionId: string,
    deviceId: string,
    fetch: boot.util.Fetch,
    getGuid: GetGuid,
    getDevMode: GetDevMode = () => false,
    customHeaders?: {
        isCustomHeadersEnabled?: boolean;
        brandName?: string;
        daznId?: string;
    },
) => {
    const HTTP_399_CODE = 399;

    if (!deviceId) {
        return Promise.reject(ERROR.deviceIdMissing());
    }
    const brand = (window?.dazn?.brand?.brandName as string)?.toUpperCase();
    const brandName = brand && brand !== BRANDS.DAZN.toUpperCase() ? brand : '';

    const isFoxtelGnc =
        (window?.dazn?.brand?.brandName as string)?.toLowerCase() ===
        BRANDS.FOXTELGNC;

    const isAppLaunch =
        isFoxtelGnc &&
        !window?.sessionStorage?.getItem(HAS_SENT_APP_LAUNCH_RAT);

    if (isAppLaunch) {
        window?.sessionStorage?.setItem(HAS_SENT_APP_LAUNCH_RAT, 'true');
    }

    return fetch(authTokenRefreshUrl, {
        method: 'POST',
        headers: getHeaders(
            `${token}`,
            getGuid,
            getDevMode,
            sessionId,
            undefined,
            customHeaders,
        ),
        body: JSON.stringify({
            DeviceId: deviceId,
            ...(brandName && { brand: brandName }),
            ...(isAppLaunch && { appLaunch: true }),
        }),
        cache: 'no-store', // absolutely no caching at all
    })
        .catch(error => Promise.reject(ERROR.profileRequestFailed(error)))
        .then(response => {
            if (response.status > HTTP_399_CODE) {
                return Promise.reject(ERROR.serverError(response.status));
            }
            if (!response.ok) {
                return Promise.reject(ERROR.responseNotOk());
            }
            return response
                .json()
                .catch(error => Promise.reject(ERROR.jsonParseFailed(error)))
                .then(
                    json =>
                        pickToken(json) ||
                        Promise.reject(ERROR.badToken(response.status)),
                );
        });
};

const NETWORK_ERRORS = {
    '401': {
        message: 'Token responded profile not authenticated',
        codeName: 'UnathorizedRefreshTokenError',
    },
    '403': {
        message: 'Country not available',
        codeName: 'RestrictedCountry',
    },
    '429': {
        message: 'Token responded not okay',
        codeName: 'ServiceTooManyAttemps',
    },
    '500': {
        message: 'Token responded not okay',
        codeName: 'GenericError',
    },
} as const;

const hasKey = (key: string): key is keyof typeof NETWORK_ERRORS =>
    key in NETWORK_ERRORS;

const ERROR = {
    deviceIdMissing: () =>
        createEraroError(
            'deviceId not defined',
            eraroCodesMap.category.RefreshTokenService,
            eraroCodesMap.code.GenericError,
            eraroCodesMap.internal.NetworkError,
            new TypeError('deviceId not defined'),
        ),
    serverError: (responseStatus: string | number) => {
        const statusString = String(responseStatus);
        const { codeName, message } = hasKey(statusString)
            ? NETWORK_ERRORS[statusString]
            : NETWORK_ERRORS['500'];

        return createEraroError(
            message,
            eraroCodesMap.category.RefreshTokenService,
            eraroCodesMap.code[codeName],
            statusString,
        );
    },
    responseNotOk: () =>
        createEraroError(
            'Token responded not okay',
            eraroCodesMap.category.RefreshTokenService,
            eraroCodesMap.code.GenericError,
            eraroCodesMap.internal.NetworkError,
        ),
    jsonParseFailed: (error: Error) =>
        createEraroError(
            'Could not convert token response to JSON',
            eraroCodesMap.category.RefreshTokenService,
            eraroCodesMap.code.GenericError,
            eraroCodesMap.internal.ParserError,
            error,
        ),
    badToken: (responseStatus: string | number) =>
        createEraroError(
            NETWORK_ERRORS['401'].message,
            eraroCodesMap.category.RefreshTokenService,
            eraroCodesMap.code[NETWORK_ERRORS['401'].codeName],
            String(responseStatus),
        ),
    profileRequestFailed: (error: Error) =>
        createEraroError(
            'Token profile request failure',
            eraroCodesMap.category.RefreshTokenService,
            eraroCodesMap.code.GenericError,
            eraroCodesMap.internal.NetworkError,
            error,
        ),
};
